"use client";

import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Audience } from "@/lib/audience-preference";

import { DotFieldCanvas } from "./dot-field-canvas";
import { boxRect, inkRects } from "./measure-ink";
import {
  CARD,
  CARD_SCALE,
  COMPANY_REVIEWS,
  EXPERT_REVIEWS,
  LEADER,
  MAX_VISIBLE,
  type Rect,
  REVIEW_CARD,
  reviewCardHeight,
} from "./panel-marks";
import { type Seat, seatsFor } from "./seats";
import type { DotField } from "./us-dot-field";
import { useImagesReady } from "./use-images-ready";
import { usePrefersReducedMotion, useRandomPops } from "./use-pop-loop";

/** The comp is drawn on a 884px-tall panel; mark sizes are in those units. */
const COMP_HEIGHT = 884;
/** How far past the copy a scrim keeps fading. */
const SCRIM_FEATHER = 130;

/** Every mark image, decoded before the loop starts. */
const ART_SOURCES = [
  ...COMPANY_REVIEWS.flatMap((review) => [review.logo, review.avatar]),
  ...EXPERT_REVIEWS.map((review) => review.avatar),
];

interface Palette {
  ground: string;
  /** Canvas dot ink as `r, g, b`, and its alpha. */
  ink: string;
  inkAlpha: number;
  pinInk: string;
  pulseInk: string;
  leaderInk: string;
}

const PALETTES: Record<Audience, Palette> = {
  expert: {
    ground: "#0a06f7",
    ink: "251, 251, 249",
    inkAlpha: 0.42,
    pinInk: "#fbfbf9",
    pulseInk: "rgba(251, 251, 249, 0.18)",
    leaderInk: "rgba(251, 251, 249, 0.34)",
  },
  researcher: {
    ground: "#0f0f0e",
    ink: "77, 111, 255",
    inkAlpha: 0.44,
    pinInk: "#fbfbf9",
    pulseInk: "rgba(77, 111, 255, 0.28)",
    leaderInk: "rgba(251, 251, 249, 0.30)",
  },
};

interface SeatedProps {
  seat: Seat;
  k: number;
  leaderLength: number;
  palette: Palette;
  shown: boolean;
  children: React.ReactNode;
}

/** A mark lifted off its lattice cell on a leader line. */
function Seated({
  seat,
  k,
  leaderLength,
  palette,
  shown,
  children,
}: SeatedProps) {
  const gap = (leaderLength + 4) * k;
  const leaderTop = seat.drop === "above" ? -gap : 4 * k;
  return (
    <div
      className="login-pop absolute"
      data-shown={shown ? "true" : "false"}
      style={{ left: seat.pin.x, top: seat.pin.y, width: 0, height: 0 }}
    >
      <span
        className="absolute"
        style={{
          left: -0.5,
          top: leaderTop,
          width: 1,
          height: leaderLength * k,
          background: palette.leaderInk,
        }}
      />
      <span
        className="login-pulse absolute rounded-full"
        style={{
          left: -13 * k,
          top: -13 * k,
          width: 26 * k,
          height: 26 * k,
          background: palette.pulseInk,
        }}
      />
      <span
        className="absolute rounded-full"
        style={{
          left: -3.5 * k,
          top: -3.5 * k,
          width: 7 * k,
          height: 7 * k,
          background: palette.pinInk,
        }}
      />
      <div
        className="login-pop-mark absolute"
        style={{
          left: seat.rect.x - seat.pin.x,
          top: seat.rect.y - seat.pin.y,
          width: seat.rect.width,
          height: seat.rect.height,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export interface DotFieldBackdropProps {
  audience: Audience;
  /**
   * The panel's copy. Their rendered ink is measured and kept clear, so the
   * marks dodge the real type ramp at every breakpoint.
   */
  headlineRef: RefObject<HTMLElement | null>;
  subtitleRef: RefObject<HTMLElement | null>;
  wordmarkRef: RefObject<HTMLElement | null>;
  /**
   * Whether the marks pop at all. A seat is chosen once, from the panel's size
   * and the copy's ink at that moment, and a mark already in flight keeps it:
   * resize the panel and that mark stays where it was, half off the new edge.
   * A caller that resizes the panel turns them off for the narrow state rather
   * than leaving them stranded. Defaults to on.
   */
  marks?: boolean;
}

/**
 * The animated half of the hero panel: a full-bleed US dot field with client
 * logos or expert reviews popping up on it, at random places in a random
 * order. Purely decorative, so it is hidden from assistive tech and takes no
 * pointer events.
 */
export function DotFieldBackdrop({
  audience,
  headlineRef,
  subtitleRef,
  wordmarkRef,
  marks = true,
}: DotFieldBackdropProps) {
  const palette = PALETTES[audience];
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [copy, setCopy] = useState<Rect[]>([]);
  const [field, setField] = useState<DotField | null>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return;
    }
    const measure = () => {
      const origin = node.getBoundingClientRect();
      setBox({ width: node.clientWidth, height: node.clientHeight });
      const zones: Rect[] = [];
      for (const ref of [headlineRef, subtitleRef]) {
        if (ref.current) {
          zones.push(...inkRects(ref.current, origin));
        }
      }
      if (wordmarkRef.current) {
        zones.push(boxRect(wordmarkRef.current, origin));
      }
      setCopy(zones);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // The copy reflows with the panel, and its own box can settle a frame late
    // once the font is in.
    const settle = window.setTimeout(measure, 250);
    return () => {
      observer.disconnect();
      window.clearTimeout(settle);
    };
  }, [headlineRef, subtitleRef, wordmarkRef]);

  // Scale off panel height: the comp is a full-viewport-height panel, so height
  // is the binding dimension.
  const k =
    box.height > 0
      ? Math.min(1.25, Math.max(0.72, box.height / COMP_HEIGHT))
      : 1;
  const onField = useCallback((next: DotField) => {
    setField(next);
  }, []);

  const seatPools = useMemo(() => {
    if (!field || copy.length === 0) {
      return [];
    }
    const query = { field, k, panel: box, copy };
    if (audience === "expert") {
      return EXPERT_REVIEWS.map(() =>
        seatsFor(
          query,
          CARD.width * CARD_SCALE,
          CARD.height * CARD_SCALE,
          LEADER.length,
          LEADER.inset,
        ),
      );
    }
    return COMPANY_REVIEWS.map((review) =>
      seatsFor(
        query,
        REVIEW_CARD.width * CARD_SCALE,
        reviewCardHeight(review.quote.length) * CARD_SCALE,
        LEADER.length,
        LEADER.inset,
      ),
    );
  }, [field, k, box, audience, copy]);

  const seatsForMark = useCallback(
    (mark: number) => seatPools[mark] ?? [],
    [seatPools],
  );
  const imagesReady = useImagesReady(ART_SOURCES);
  const pops = useRandomPops(
    seatPools.length,
    seatsForMark,
    imagesReady && seatPools.some((pool) => pool.length > 0),
    reduced,
    MAX_VISIBLE[audience],
  );
  // Both sides carry a review card now, so one leader length serves both.
  const leaderLength = LEADER.length;
  // The card's own scale. `Seated` sizes its box from the seat, which was
  // reserved at this scale too, so the two agree.
  const cardK = k * CARD_SCALE;

  // Copy sits in at most two clusters: the wordmark and headline near the top,
  // the subtitle either under them or at the panel foot. Guard each where it is.
  const scrims = useMemo(() => {
    if (box.height === 0 || copy.length === 0) {
      return { top: 0, bottom: 0 };
    }
    const middle = box.height * 0.55;
    const upper = copy.filter((r) => r.y + r.height / 2 <= middle);
    const lower = copy.filter((r) => r.y + r.height / 2 > middle);
    const feather = SCRIM_FEATHER * k;
    return {
      top: upper.length
        ? Math.min(
            box.height,
            Math.max(...upper.map((r) => r.y + r.height)) + feather,
          )
        : 0,
      bottom: lower.length
        ? Math.min(
            box.height,
            box.height - Math.min(...lower.map((r) => r.y)) + feather,
          )
        : 0,
    };
  }, [box.height, copy, k]);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden select-none"
    >
      {/* Hoisted and deduped by React, so it ships wherever the backdrop is
          used rather than living in globals.css for one component.

          Transitions, not keyframes: a keyframed exit plays its opening frame
          on mount, which makes a mark flash opaque before fading in. Nothing
          scales either, because scaling a container of text resamples every
          glyph each frame and reads as a shimmer. The group fades and the mark
          alone rises; the pin stays on its cell. */}
      <style href="terac-hero-dot-field" precedence="default">{`
        .login-pop { opacity: 0; transition: opacity 260ms ease-in; }
        .login-pop[data-shown="true"] {
          opacity: 1;
          transition: opacity 420ms ease-out;
        }
        .login-pop-mark {
          transform: translateY(12px);
          will-change: transform;
          transition: transform 260ms ease-in;
        }
        .login-pop[data-shown="true"] .login-pop-mark {
          transform: translateY(0);
          transition: transform 620ms cubic-bezier(0.16, 0.84, 0.24, 1);
        }
        .login-pulse {
          animation: login-pulse 2800ms ease-in-out 700ms infinite;
        }
        @keyframes login-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.16); }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-pop { opacity: 1; transition: none; }
          .login-pop-mark { transform: none; transition: none; }
          .login-pulse { animation: none; }
        }
      `}</style>

      <DotFieldCanvas
        width={box.width}
        height={box.height}
        scale={k}
        ink={palette.ink}
        alpha={palette.inkAlpha}
        onField={onField}
      />

      {/* One band per copy cluster, sized from what was measured. The shipped
          panel anchors its subtitle to the panel foot while the comp put it
          under the headline, so a fixed top strip protects one and not the
          other. */}
      {scrims.top > 0 ? (
        <div
          className="absolute inset-x-0 top-0"
          style={{
            height: scrims.top,
            background: `linear-gradient(to bottom, ${palette.ground} 0%, ${palette.ground} 62%, transparent 100%)`,
            opacity: 0.74,
          }}
        />
      ) : null}
      {scrims.bottom > 0 ? (
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: scrims.bottom,
            background: `linear-gradient(to top, ${palette.ground} 0%, ${palette.ground} 55%, transparent 100%)`,
            opacity: 0.74,
          }}
        />
      ) : null}

      {/* Kept mounted so every mark image stays decoded. A card that mounts
          with an undecoded image paints blank for a frame. */}
      <div className="absolute h-0 w-0 overflow-hidden opacity-0">
        {ART_SOURCES.map((src) => (
          <img
            key={src}
            src={src}
            alt=""
            width={1}
            height={1}
            decoding="sync"
          />
        ))}
      </div>

      {(marks ? pops : []).map((pop) => {
        const review = EXPERT_REVIEWS[pop.mark];
        const company = COMPANY_REVIEWS[pop.mark];
        return (
          <Seated
            key={`${pop.slot}-${pop.cycle}-${pop.seat.city}`}
            seat={pop.seat}
            k={k}
            leaderLength={leaderLength}
            palette={palette}
            shown={pop.shown}
          >
            {audience === "expert" && review ? (
              <ReviewCard review={review} k={cardK} />
            ) : null}
            {audience === "researcher" && company ? (
              <CompanyReviewCard review={company} k={cardK} />
            ) : null}
          </Seated>
        );
      })}
    </div>
  );
}

/** The chosen V11 card: a blue quote mark, the review, then identity and stat. */
function ReviewCard({
  review,
  k,
}: {
  review: (typeof EXPERT_REVIEWS)[number];
  k: number;
}) {
  return (
    <div
      className="relative h-full w-full"
      style={{
        background: "#fbfbf9",
        borderRadius: 14 * k,
        boxShadow: `0 ${10 * k}px ${28 * k}px rgba(0, 0, 0, 0.3)`,
      }}
    >
      <span
        className="absolute font-medium"
        style={{
          left: 16 * k,
          top: 12 * k,
          fontSize: 30 * k,
          lineHeight: `${30 * k}px`,
          color: "#0a06f7",
        }}
      >
        {"\u201C"}
      </span>
      {review.quote.map((line, index) => (
        <span
          key={line}
          className="absolute whitespace-nowrap"
          style={{
            left: 38 * k,
            top: (16 + index * 18) * k,
            fontSize: 12.5 * k,
            lineHeight: `${18 * k}px`,
            letterSpacing: "-0.01em",
            color: "#1c1c1a",
          }}
        >
          {line}
        </span>
      ))}
      <img
        src={review.avatar}
        alt=""
        draggable={false}
        decoding="sync"
        loading="eager"
        className="absolute rounded-full object-cover"
        style={{
          left: 16 * k,
          top: 69 * k,
          width: 28 * k,
          height: 28 * k,
          objectPosition: review.avatarPosition,
        }}
      />
      <span
        className="absolute font-medium whitespace-nowrap"
        style={{
          left: 52 * k,
          top: 68 * k,
          fontSize: 13 * k,
          lineHeight: `${17 * k}px`,
          letterSpacing: "-0.015em",
          color: "#0f0f0e",
        }}
      >
        {review.name}
      </span>
      <span
        className="absolute font-mono whitespace-nowrap"
        style={{
          left: 52 * k,
          top: 86 * k,
          fontSize: 8 * k,
          lineHeight: `${11 * k}px`,
          letterSpacing: "0.1em",
          color: "#8a8a86",
        }}
      >
        {review.role}
      </span>
      <span
        className="absolute text-right font-medium whitespace-nowrap"
        style={{
          right: 16 * k,
          top: 69 * k,
          fontSize: 12 * k,
          lineHeight: `${15 * k}px`,
          letterSpacing: "-0.015em",
          color: "#0f0f0e",
        }}
      >
        {review.statValue}
      </span>
      <span
        className="absolute text-right font-mono whitespace-nowrap"
        style={{
          right: 16 * k,
          top: 87 * k,
          fontSize: 7 * k,
          lineHeight: `${10 * k}px`,
          letterSpacing: "0.12em",
          color: "#8a8a86",
        }}
      >
        {review.statLabel}
      </span>
    </div>
  );
}

/** A customer review, sized to its own quote. Used on the researcher panel. */
function CompanyReviewCard({
  review,
  k,
}: {
  review: (typeof COMPANY_REVIEWS)[number];
  k: number;
}) {
  const identityTop =
    REVIEW_CARD.quoteTop +
    review.quote.length * REVIEW_CARD.lineHeight +
    REVIEW_CARD.gap;
  return (
    <div
      className="relative h-full w-full"
      style={{
        background: "#fbfbf9",
        borderRadius: 13 * k,
        boxShadow: `0 ${10 * k}px ${28 * k}px rgba(0, 0, 0, 0.45)`,
      }}
    >
      <span
        className="absolute font-medium"
        style={{
          left: 14 * k,
          top: 10 * k,
          fontSize: 26 * k,
          lineHeight: `${26 * k}px`,
          color: "#0a06f7",
        }}
      >
        “
      </span>
      {review.quote.map((line, index) => (
        <span
          key={line}
          className="absolute whitespace-nowrap"
          style={{
            left: 34 * k,
            top: (REVIEW_CARD.quoteTop + index * REVIEW_CARD.lineHeight) * k,
            fontSize: 11.5 * k,
            lineHeight: `${REVIEW_CARD.lineHeight * k}px`,
            letterSpacing: "-0.01em",
            color: "#1c1c1a",
          }}
        >
          {line}
        </span>
      ))}
      <img
        src={review.avatar}
        alt=""
        draggable={false}
        decoding="sync"
        loading="eager"
        className="absolute rounded-full object-cover"
        style={{
          left: 14 * k,
          top: identityTop * k,
          width: REVIEW_CARD.avatar * k,
          height: REVIEW_CARD.avatar * k,
          objectPosition: review.avatarPosition,
        }}
      />
      <span
        className="absolute font-medium whitespace-nowrap"
        style={{
          left: 46 * k,
          top: (identityTop - 2) * k,
          fontSize: 12 * k,
          lineHeight: `${16 * k}px`,
          letterSpacing: "-0.015em",
          color: "#0f0f0e",
        }}
      >
        {review.name}
      </span>
      <span
        className="absolute font-mono whitespace-nowrap"
        style={{
          left: 46 * k,
          top: (identityTop + 14) * k,
          fontSize: 7.5 * k,
          lineHeight: `${10 * k}px`,
          letterSpacing: "0.1em",
          color: "#8a8a86",
        }}
      >
        {review.title}
      </span>
      <img
        src={review.logo}
        alt=""
        draggable={false}
        decoding="sync"
        loading="eager"
        className="absolute"
        style={{
          right: 14 * k,
          top: (identityTop + (REVIEW_CARD.avatar - review.logoHeight) / 2) * k,
          width: review.logoWidth * k,
          height: review.logoHeight * k,
        }}
      />
    </div>
  );
}
